import { DoctorRole } from '@prisma/client';

/**
 * Role configuration and display mappings
 * Single source of truth for role labels and selectable options
 */

/**
 * Maps internal DoctorRole enum values to user-friendly display labels
 */
export const ROLE_DISPLAY_LABELS: Record<DoctorRole, string> = {
  // Current roles with user-friendly labels
  OWNER_DENTIST: 'Owner Dentist',
  ASSOCIATE_DENTIST: 'Associate Dentist',
  OFFICE_MANAGER: 'Office Manager',
  RDH: 'RDH',
  FRONT_DESK: 'Front Desk',
  DENTAL_ASSISTANT: 'Dental Assistant',
  STERILIZATION_TECH: 'Sterilization Tech',
  OTHER: 'Other',

  // Legacy roles - still mapped for existing users but not selectable
  OWNER: 'Owner',
  ASSOCIATE: 'Associate',
  PARTNER: 'Partner',
};

/**
 * Roles that are actively selectable in forms
 * Excludes legacy roles (OWNER, ASSOCIATE, PARTNER)
 */
export const SELECTABLE_ROLES: DoctorRole[] = [
  DoctorRole.OWNER_DENTIST,
  DoctorRole.ASSOCIATE_DENTIST,
  DoctorRole.OFFICE_MANAGER,
  DoctorRole.RDH,
  DoctorRole.FRONT_DESK,
  DoctorRole.DENTAL_ASSISTANT,
  DoctorRole.STERILIZATION_TECH,
  DoctorRole.OTHER,
];

/**
 * Dentist roles that require additional professional setup
 * (insurance carriers, membership plans, etc.)
 */
export const DENTIST_ROLES: DoctorRole[] = [DoctorRole.OWNER_DENTIST, DoctorRole.ASSOCIATE_DENTIST];

/**
 * Owner roles that have full administrative permissions
 */
export const OWNER_ROLES: DoctorRole[] = [DoctorRole.OWNER, DoctorRole.OWNER_DENTIST];

export interface RoleOption {
  value: DoctorRole;
  label: string;
}

/**
 * Get selectable role options with user-friendly labels
 * Filters based on current user's permissions
 *
 * @param currentUserRole - The role of the current user
 * @returns Array of role options with value and label
 */
export function getSelectableRoleOptions(currentUserRole?: string): RoleOption[] {
  let availableRoles = [...SELECTABLE_ROLES];

  // Only owners can invite other owners
  if (currentUserRole && !OWNER_ROLES.includes(currentUserRole as DoctorRole)) {
    availableRoles = availableRoles.filter(role => !OWNER_ROLES.includes(role));
  }

  return availableRoles.map(role => ({
    value: role,
    label: ROLE_DISPLAY_LABELS[role],
  }));
}

/**
 * Get the display label for a role
 *
 * @param role - The role to get the display label for
 * @returns The user-friendly display label
 */
export function getRoleDisplayLabel(role: DoctorRole | string): string {
  return ROLE_DISPLAY_LABELS[role as DoctorRole] || role;
}
